import { z } from 'zod';
import { posClient } from './client';

/**
 * Order notifications on this device (pos-api PushController).
 *
 * The subscription is minted by the browser's own push service and stored per
 * device, so the same person on a phone and a desktop is two subscriptions.
 * The website does the sending when an order is created.
 */

const wireKeySchema = z.looseObject({
  success: z.literal(true),
  data: z.looseObject({
    public_key: z.string(),
    available: z.boolean().default(true),
  }),
});

const wireTestSchema = z.looseObject({
  success: z.literal(true),
  data: z.looseObject({ sent: z.number() }),
});

export interface PushRequestOptions {
  signal?: AbortSignal;
}

export const pushApi = {
  /** 503 when the platform has no VAPID key or this tenant has no table yet. */
  async key(options: PushRequestOptions = {}): Promise<{ publicKey: string; available: boolean }> {
    const res = await posClient.get('/push/key', { signal: options.signal });
    const body = wireKeySchema.parse(res.data).data;
    return { publicKey: body.public_key, available: body.available };
  },

  async subscribe(
    subscription: PushSubscriptionJSON,
    userAgent: string,
    options: PushRequestOptions = {},
  ): Promise<void> {
    await posClient.post(
      '/push/subscribe',
      { subscription, user_agent: userAgent },
      { signal: options.signal },
    );
  },

  async unsubscribe(endpoint: string, options: PushRequestOptions = {}): Promise<void> {
    await posClient.post('/push/unsubscribe', { endpoint }, { signal: options.signal });
  },

  /** 422 when nothing is subscribed, 502 when the push service refused every device. */
  async test(options: PushRequestOptions = {}): Promise<number> {
    const res = await posClient.post('/push/test', {}, { signal: options.signal });
    const body = wireTestSchema.parse(res.data).data;
    return body.sent;
  },
};
